import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { LayoutService } from '@core/services/layout.service';
import { AuthService } from '@core/auth/services/auth.service';
import { expandCollapse } from '@core/animations/route.animations';
import { NavGroup, NavItem } from '@core/models';

@Component({
  selector: 'app-sidebar',
  imports: [MatIconModule, MatTooltipModule, RouterLink, RouterLinkActive],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [expandCollapse],
  templateUrl: './sidebar.html',
})
export class Sidebar {
  readonly layout = inject(LayoutService);
  readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly currentUrl = signal<string>('');
  readonly expandedItems = signal<Set<string>>(new Set());

  constructor() {
    this.currentUrl.set(this.router.url);
    this.router.events.subscribe(() => {
      const url = this.router.url;
      this.currentUrl.set(url);
      if (url.includes('/pagamento')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Pagamento');
          return next;
        });
      }
      if (url.includes('/capacitacao')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Capacitação');
          return next;
        });
      }
      if (url.includes('/instrutoria')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Instrutoria');
          return next;
        });
      }
      if (url.includes('/avaliacao-desempenho')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Avaliação de Desempenho');
          return next;
        });
      }
      if (url.includes('/mapa-forca')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Mapa de Força');
          return next;
        });
      }
      if (url.includes('/banco-talentos')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Banco de Talentos');
          return next;
        });
      }
      if (url.includes('/auditoria')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Segurança e Auditoria');
          return next;
        });
      }
      if (url.includes('/bem-estar')) {
        this.expandedItems.update(set => {
          const next = new Set(set);
          next.add('Bem-Estar');
          return next;
        });
      }
    });

    if (this.router.url.includes('/pagamento')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Pagamento');
        return next;
      });
    }
    if (this.router.url.includes('/capacitacao')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Capacitação');
        return next;
      });
    }
    if (this.router.url.includes('/instrutoria')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Instrutoria');
        return next;
      });
    }
    if (this.router.url.includes('/avaliacao-desempenho')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Avaliação de Desempenho');
        return next;
      });
    }
    if (this.router.url.includes('/mapa-forca')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Mapa de Força');
        return next;
      });
    }
    if (this.router.url.includes('/banco-talentos')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Banco de Talentos');
        return next;
      });
    }
    if (this.router.url.includes('/auditoria')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Segurança e Auditoria');
        return next;
      });
    }
    if (this.router.url.includes('/bem-estar')) {
      this.expandedItems.update(set => {
        const next = new Set(set);
        next.add('Bem-Estar');
        return next;
      });
    }
  }

  readonly menuGroups = signal<NavGroup[]>([
    {
      category: 'GESTÃO DE PESSOAL',
      roles: [
        'ROLE_CADASTRO_VISUALIZAR',
        'ROLE_ORGANIZACAO_VISUALIZAR',
        'ROLE_ORGANIZACAO_SIMULAR',
        'ROLE_MOVIMENTACAO_USUARIO',
        'ROLE_MOVIMENTACAO_CMT',
        'ROLE_MOVIMENTACAO_GESTOR',
        'ROLE_MOVIMENTACAO_CONSULTA',
        'ROLE_MOVIMENTACAO_VETERANOS',
        'ROLE_MOVIMENTACAO_SCAF',
        'ROLE_AFASTAMENTO_VIEW',
        'ROLE_AFASTAMENTO_VIEW_PROPRIO',
        'ROLE_AFASTAMENTO_GESTOR_DPM',
        'ROLE_AFASTAMENTO_GESTOR_UPM',
        'ROLE_TEMPO_SERVICO_VISUALIZAR_COMPLETO',
        'ROLE_TEMPO_SERVICO_DASHBOARD',
        'ROLE_ASSENTAMENTOS_VISUALIZAR',
        'ROLE_ASSENTAMENTOS_GESTOR',
        'ROLE_ASSENTAMENTOS_OPERADOR',
        'ROLE_ASSENTAMENTOS_DASHBOARD',
        'ROLE_PROMOCAO_VISUALIZAR_QUADRO_ANTIGUIDADE',
        'ROLE_PROMOCAO_GERENCIAR_QUADRO_ANTIGUIDADE',
        'ROLE_PROMOCAO_VISUALIZAR_MERECIMENTO',
        'ROLE_PROMOCAO_GERENCIAR_MERECIMENTO',
        'ROLE_PROMOCAO_VISUALIZAR_PROCESSOS_BRAVURA',
        'ROLE_PAGAMENTO_LEITURA',
        'ROLE_PAGAMENTO_ESCRITA',
        'ROLE_PAGAMENTO_EXPORTACAO',
        'ROLE_PAGAMENTO_EXPORTACAO_SAUDE',
        'ROLE_BOLETIM_VISUALIZAR',
        'ROLE_BOLETIM_REVISAR',
        'ROLE_BOLETIM_ASSINAR',
        'ROLE_BOLETIM_RETIFICAR',
        'ROLE_BOLETIM_PUBLICAR',
        'ROLE_CAPACITACAO_VISUALIZAR',
        'ROLE_CLASSIFICACAO_UNIDADE',
        'ROLE_CLASSIFICACAO_SUBUNIDADE',
        'ROLE_CLASSIFICACAO_PLT',
        'ROLE_CLASSIFICACAO_CONSULTA_GLOBAL',
        'ROLE_CLASSIFICACAO_PRONTIDAO',
        'ROLE_CLASSIFICACAO_PRONTIDAO_PARCIAL',
        'ROLE_APTIDAO_GESTOR_CCF',
        'ROLE_APTIDAO_AVALIADOR',
        'ROLE_APTIDAO_PRESIDENTE',
        'ROLE_APTIDAO_AUDITOR',
        'ROLE_AVAL_DESEMP_HOMOLOGAR',
        'ROLE_AVAL_DESEMP_AVALIAR',
        'ROLE_AVAL_DESEMP_AUTOAVALIAR',
        'ROLE_AVAL_DESEMP_VISUALIZAR_PROPRIO',
        'ROLE_BANCO_TALENTOS_AUTODECLARAR',
        'ROLE_BANCO_TALENTOS_CONSULTAR',
        'ROLE_INSTRUTORIA_VISUALIZAR',
        'ROLE_INSTRUTORIA_HABILITAR_INSTRUTOR',
        'ROLE_INSTRUTORIA_REGISTRAR_CARGA',
        'ROLE_INSTRUTORIA_AVALIAR_CURSO',
        'ROLE_BEM_ESTAR_PROPRIO',
        'ROLE_BEM_ESTAR_PROFISSIONAL',
        'ROLE_BEM_ESTAR_GESTOR',
        'ROLE_MAPAFORCA_VISUALIZAR_UNIDADE',
        'ROLE_MAPAFORCA_VISUALIZAR_GLOBAL',
        'ROLE_MAPAFORCA_COMANDO_GERAL',
        'ROLE_MAPAFORCA_BUSCA_OPERACIONAL'
      ],
      items: [
        {
          icon: 'hotel',
          label: 'Afastamento',
          roles: ['ROLE_AFASTAMENTO_VIEW', 'ROLE_AFASTAMENTO_VIEW_PROPRIO', 'ROLE_AFASTAMENTO_GESTOR_DPM', 'ROLE_AFASTAMENTO_GESTOR_UPM'],
          children: [
            { icon: 'dashboard', label: 'Dashboard', route: '/afastamento/dashboard', roles: [] },
            { icon: 'workspaces', label: 'Workspace', route: '/afastamento', roles: ['ROLE_AFASTAMENTO_VIEW'] }
          ],
        },
        {
          icon: 'fitness_center',
          label: 'Aptidão Física',
          roles: [
            'ROLE_USER',
            'ROLE_APTIDAO_GESTOR_CCF',
            'ROLE_APTIDAO_AVALIADOR',
            'ROLE_APTIDAO_PRESIDENTE',
            'ROLE_APTIDAO_AUDITOR'
          ],
          children: [
            { icon: 'workspaces', label: 'Área de Trabalho', route: '/aptidao-fisica/workspace', roles: ['ROLE_APTIDAO_GESTOR_CCF', 'ROLE_APTIDAO_AVALIADOR', 'ROLE_APTIDAO_PRESIDENTE', 'ROLE_APTIDAO_AUDITOR'] },
            { icon: 'person', label: 'Meu Painel (Portal)', route: '/aptidao-fisica/meu-painel', roles: ['ROLE_USER'] },
            { icon: 'event', label: 'Gestão de Agendas', route: '/aptidao-fisica/agendas', roles: ['ROLE_APTIDAO_GESTOR_CCF'] },
            { icon: 'fact_check', label: 'Controle de Presença', route: '/aptidao-fisica/agendas/presenca-geral', roles: ['ROLE_APTIDAO_AVALIADOR'] },
            { icon: 'verified', label: 'Homologação e Ata', route: '/aptidao-fisica/homologacao', roles: ['ROLE_APTIDAO_PRESIDENTE'] },
            { icon: 'gavel', label: 'Painel de Auditoria', route: '/aptidao-fisica/auditoria', roles: ['ROLE_APTIDAO_AUDITOR'] },
            { icon: 'dashboard', label: 'Dashboard do Comando', route: '/aptidao-fisica/dashboard-comando', roles: ['ROLE_APTIDAO_GESTOR_CCF', 'ROLE_APTIDAO_PRESIDENTE'] }
          ]
        },
        {
          icon: 'folder_open',
          label: 'Assentamentos',
          roles: ['ROLE_ASSENTAMENTOS_VISUALIZAR', 'ROLE_ASSENTAMENTOS_GESTOR', 'ROLE_ASSENTAMENTOS_OPERADOR', 'ROLE_ASSENTAMENTOS_DASHBOARD'],
          children: [
            { icon: 'workspaces', label: 'Workspace', route: '/assentamentos/workspace', roles: [] },
            { icon: 'dashboard', label: 'Dashboard', route: '/assentamentos/dashboard', roles: [] },
            { icon: 'search', label: 'Consulta', route: '/assentamentos/lancamentos', roles: [] },
            { icon: 'add_circle', label: 'Novo Assentamento', route: '/assentamentos/lancamentos/novo', roles: [] },
            { icon: 'description', label: 'Extrato Funcional', route: '/assentamentos/extrato-funcional', roles: [] },
            { icon: 'shield', label: 'Governança (LGPD)', route: '/assentamentos/governanca', roles: [] },
            { icon: 'warning', label: 'Divergências', route: '/assentamentos/divergencias', roles: [] },
            { icon: 'sync', label: 'Integrações', route: '/assentamentos/integracoes', roles: [] }
          ]
        },
        {
          icon: 'rate_review',
          label: 'Avaliação de Desempenho',
          roles: [
            'ROLE_AVAL_DESEMP_HOMOLOGAR',
            'ROLE_AVAL_DESEMP_AVALIAR',
            'ROLE_AVAL_DESEMP_AUTOAVALIAR',
            'ROLE_AVAL_DESEMP_VISUALIZAR_PROPRIO'
          ],
          children: [
            { icon: 'workspaces', label: 'Minha Equipe (Chefia)', route: '/avaliacao-desempenho/workspace-chefia', roles: ['ROLE_AVAL_DESEMP_AVALIAR'] },
            { icon: 'person', label: 'Meu Extrato', route: '/avaliacao-desempenho/meu-extrato', roles: ['ROLE_AVAL_DESEMP_VISUALIZAR_PROPRIO'] },
            { icon: 'campaign', label: 'Gestão de Ciclos (DGP)', route: '/avaliacao-desempenho/workspace-dgp', roles: ['ROLE_AVAL_DESEMP_HOMOLOGAR'] },
            { icon: 'assignment_turned_in', label: 'PDI', route: '/avaliacao-desempenho/pdi', roles: ['ROLE_AVAL_DESEMP_AUTOAVALIAR', 'ROLE_AVAL_DESEMP_AVALIAR'] }
          ]
        },
        {
          icon: 'stars',
          label: 'Banco de Talentos',
          roles: ['ROLE_BANCO_TALENTOS_AUTODECLARAR', 'ROLE_BANCO_TALENTOS_CONSULTAR', 'ROLE_USER'],
          children: [
            { icon: 'account_circle', label: 'Meu Perfil', route: '/banco-talentos/meu-perfil', roles: ['ROLE_BANCO_TALENTOS_AUTODECLARAR', 'ROLE_USER'] },
            { icon: 'search', label: 'Busca de Talentos', route: '/banco-talentos/busca', roles: ['ROLE_BANCO_TALENTOS_CONSULTAR'] }
          ]
        },
        {
          icon: 'favorite',
          label: 'Bem-Estar',
          roles: ['ROLE_BEM_ESTAR_PROPRIO', 'ROLE_BEM_ESTAR_PROFISSIONAL', 'ROLE_BEM_ESTAR_GESTOR'],
          children: [
            { icon: 'person', label: 'Meu Bem-Estar', route: '/bem-estar', roles: ['ROLE_BEM_ESTAR_PROPRIO'] },
            { icon: 'pending_actions', label: 'Fila de Triagem', route: '/bem-estar/triagem', roles: ['ROLE_BEM_ESTAR_PROFISSIONAL'] },
            { icon: 'analytics', label: 'Dashboard Gerencial', route: '/bem-estar/dashboard', roles: ['ROLE_BEM_ESTAR_GESTOR'] }
          ]
        },
        {
          icon: 'article',
          label: 'Boletim',
          roles: ['ROLE_BOLETIM_VISUALIZAR', 'ROLE_BOLETIM_REVISAR', 'ROLE_BOLETIM_ASSINAR', 'ROLE_BOLETIM_PUBLICAR'],
          children: [
            { icon: 'dashboard', label: 'Dashboard', route: '/boletim', roles: [] },
            { icon: 'rate_review', label: 'Fila de Revisão', route: '/boletim/revisao', roles: ['ROLE_BOLETIM_REVISAR'] },
            { icon: 'draw', label: 'Fila de Assinatura', route: '/boletim/assinatura', roles: ['ROLE_BOLETIM_ASSINAR'] },
            { icon: 'auto_stories', label: 'Fechamento de Boletim', route: '/boletim/publicacao', roles: ['ROLE_BOLETIM_PUBLICAR'] },
            { icon: 'search', label: 'Consulta de Boletins', route: '/boletim/consulta', roles: ['ROLE_BOLETIM_VISUALIZAR'] }
          ]
        },
        {
          icon: 'person',
          label: 'Cadastro',
          roles: ['ROLE_CADASTRO_VISUALIZAR'],
          children: [
            { label: 'OPERAÇÃO', isHeader: true },
            { icon: 'grid_view', label: 'Dashboard', route: '/cadastro/dashboard', roles: ['ROLE_CADASTRO_DASHBOARD_VISUALIZAR'] },
            { icon: 'dns', label: 'Pessoas & Vínculos', route: '/cadastro', roles: ['ROLE_CADASTRO_VISUALIZAR'] },
            { icon: 'history', label: 'Recadastramento', route: '/cadastro/recadastramento', roles: ['ROLE_CADASTRO_RECADASTRAMENTO_GERENCIAR'] },
            { icon: 'adjust', label: 'Coleta Presencial', route: '/cadastro/coleta', roles: ['ROLE_CADASTRO_VISUALIZAR'] },
            { label: 'VÍNCULOS', isHeader: true },
            { icon: 'analytics', label: 'Dashboard Dependentes', route: '/cadastro/dashboard', queryParams: { tab: 'dependentes' }, roles: ['ROLE_CADASTRO_VISUALIZAR'] },
            { icon: 'diamond', label: 'Dependentes', route: '/cadastro/dependentes', roles: ['ROLE_CADASTRO_VISUALIZAR'] },
            { icon: 'add', label: 'Pensionistas', route: '/cadastro/pensionistas', roles: ['ROLE_CADASTRO_VISUALIZAR'] },
            { label: 'GOVERNANÇA', isHeader: true },
            { icon: 'shield', label: 'Auditoria & LGPD', route: '/cadastro/auditoria', roles: ['ROLE_CADASTRO_VISUALIZAR'] },
          ],
        },
        {
          icon: 'school',
          label: 'Capacitação',
          roles: ['ROLE_CAPACITACAO_VISUALIZAR'],
          children: [
            { icon: 'workspaces', label: 'Workspace', route: '/capacitacao', roles: [] },
            { icon: 'list_alt', label: 'Catálogo de Cursos', route: '/capacitacao/cursos', roles: [] },
            { icon: 'assignment_ind', label: 'Atividades e Funções', route: '/capacitacao/atividades', roles: [] },
            { icon: 'history', label: 'Histórico de Habilitações', route: '/capacitacao/historico', roles: [] },
            { icon: 'rule', label: 'Elegibilidade', route: '/capacitacao/elegibilidade', roles: [] },
            { icon: 'cloud_upload', label: 'Importação de Dados', route: '/capacitacao/importar', roles: [] }
          ]
        },
        {
          icon: 'assignment_turned_in',
          label: 'Classificação',
          roles: [
            'ROLE_CLASSIFICACAO_UNIDADE',
            'ROLE_CLASSIFICACAO_SUBUNIDADE',
            'ROLE_CLASSIFICACAO_PLT',
            'ROLE_CLASSIFICACAO_CONSULTA_GLOBAL',
            'ROLE_CLASSIFICACAO_PRONTIDAO',
            'ROLE_CLASSIFICACAO_PRONTIDAO_PARCIAL'
          ],
          children: [
            { icon: 'workspaces', label: 'Workspace', route: '/classificacao', roles: [] },
            { icon: 'dashboard', label: 'Dashboard', route: '/classificacao/dashboard', roles: ['ROLE_CLASSIFICACAO_UNIDADE', 'ROLE_CLASSIFICACAO_SUBUNIDADE'] },
            { icon: 'list', label: 'Lotação Nominal', route: '/classificacao/nominal', roles: ['ROLE_CLASSIFICACAO_SUBUNIDADE', 'ROLE_CLASSIFICACAO_PLT'] },
            { icon: 'add_circle', label: 'Registrar Classificação', route: '/classificacao/registrar', roles: ['ROLE_CLASSIFICACAO_UNIDADE'] },
            { icon: 'gavel', label: 'Trilha de Auditoria', route: '/classificacao/auditoria', roles: ['ROLE_CLASSIFICACAO_UNIDADE'] },
            { icon: 'trending_up', label: 'Central Analítica', route: '/classificacao/analytics', roles: ['ROLE_CLASSIFICACAO_UNIDADE'] }
          ]
        },
        {
          icon: 'local_library',
          label: 'Instrutoria',
          roles: [
            'ROLE_INSTRUTORIA_VISUALIZAR',
            'ROLE_INSTRUTORIA_HABILITAR_INSTRUTOR',
            'ROLE_INSTRUTORIA_REGISTRAR_CARGA',
            'ROLE_INSTRUTORIA_AVALIAR_CURSO'
          ],
          children: [
            { icon: 'dashboard', label: 'Workspace', route: '/instrutoria/workspace', roles: ['ROLE_INSTRUTORIA_VISUALIZAR'] },
            { icon: 'list_alt', label: 'Catálogo de Instrutores', route: '/instrutoria/catalogo', roles: ['ROLE_INSTRUTORIA_VISUALIZAR'] },
            { icon: 'assignment_ind', label: 'Alocação de Turmas', route: '/instrutoria/alocacao', roles: ['ROLE_INSTRUTORIA_HABILITAR_INSTRUTOR'] },
            { icon: 'calendar_month', label: 'Minha Agenda', route: '/instrutoria/agenda', roles: ['ROLE_INSTRUTORIA_REGISTRAR_CARGA'] },
            { icon: 'rate_review', label: 'Avaliações Pendentes', route: '/instrutoria/avaliacoes-pendentes', roles: ['ROLE_INSTRUTORIA_AVALIAR_CURSO'] },
            { icon: 'analytics', label: 'Produtividade Docente', route: '/instrutoria/relatorios', roles: ['ROLE_INSTRUTORIA_VISUALIZAR'] }
          ]
        },
        {
          icon: 'military_tech',
          label: 'Mapa de Força',
          roles: [
            'ROLE_MAPAFORCA_VISUALIZAR_UNIDADE',
            'ROLE_MAPAFORCA_VISUALIZAR_GLOBAL',
            'ROLE_MAPAFORCA_COMANDO_GERAL',
            'ROLE_MAPAFORCA_BUSCA_OPERACIONAL',
          ],
          children: [
            { icon: 'dashboard', label: 'Dashboard Estratégico', route: '/mapa-forca/dashboard', roles: ['ROLE_MAPAFORCA_COMANDO_GERAL'] },
            { icon: 'list', label: 'Visão Global', route: '/mapa-forca/visao-global', roles: ['ROLE_MAPAFORCA_VISUALIZAR_GLOBAL'] },
            { icon: 'workspaces', label: 'Minha Unidade', route: '/mapa-forca/minha-unidade', roles: ['ROLE_MAPAFORCA_VISUALIZAR_UNIDADE'] },
            { icon: 'search', label: 'Busca Operacional', route: '/mapa-forca/busca-operacional', roles: ['ROLE_MAPAFORCA_BUSCA_OPERACIONAL'] }
          ]
        },
        {
          icon: 'swap_horiz',
          label: 'Movimentação',
          roles: ['ROLE_MOVIMENTACAO_USUARIO', 'ROLE_MOVIMENTACAO_CMT', 'ROLE_MOVIMENTACAO_GESTOR', 'ROLE_MOVIMENTACAO_CONSULTA', 'ROLE_MOVIMENTACAO_VETERANOS', 'ROLE_MOVIMENTACAO_SCAF'],
          children: [
            { icon: 'search', label: 'Pesquisa Avançada', route: '/movimentacao', roles: ['ROLE_MOVIMENTACAO_CONSULTA', 'ROLE_MOVIMENTACAO_GESTOR'] },
            { icon: 'add', label: 'Nova Movimentação', route: '/movimentacao/novo', roles: ['ROLE_MOVIMENTACAO_GESTOR'] },
            { icon: 'assignment_ind', label: 'Nomeações para Cargo', route: '/movimentacao/nomeacao-cargo', roles: ['ROLE_MOVIMENTACAO_GESTOR', 'ROLE_MOVIMENTACAO_CONSULTA'] },
            { icon: 'group_add', label: 'Em Lote', route: '/movimentacao/lote', roles: ['ROLE_MOVIMENTACAO_GESTOR'] },
            { icon: 'rule', label: 'Requerimentos', route: '/movimentacao/requerimentos', roles: ['ROLE_MOVIMENTACAO_USUARIO', 'ROLE_MOVIMENTACAO_CMT', 'ROLE_MOVIMENTACAO_GESTOR'] },
            { icon: 'manage_accounts', label: 'Gestão de Claros', route: '/movimentacao/claros', roles: ['ROLE_MOVIMENTACAO_CONSULTA', 'ROLE_MOVIMENTACAO_GESTOR', 'ROLE_MOVIMENTACAO_SCAF'] },
            { icon: 'hub', label: 'Organograma', route: '/movimentacao/organograma', roles: ['ROLE_MOVIMENTACAO_CONSULTA', 'ROLE_MOVIMENTACAO_GESTOR'] },
            { icon: 'analytics', label: 'Dashboard', route: '/movimentacao/dashboard', roles: ['ROLE_MOVIMENTACAO_CONSULTA', 'ROLE_MOVIMENTACAO_VETERANOS', 'ROLE_MOVIMENTACAO_SCAF'] },
            { icon: 'admin_panel_settings', label: 'Painel SCAF', route: '/afastamento/scaf', roles: ['ROLE_AFASTAMENTO_GESTOR_DPM'] },
            { icon: 'elderly', label: 'Dashboard DVPC', route: '/movimentacao/dvpc', roles: ['ROLE_MOVIMENTACAO_VETERANOS', 'ROLE_MOVIMENTACAO_GESTOR'] },
          ],
        },
        {
          icon: 'corporate_fare',
          label: 'Organização',
          roles: ['ROLE_ORGANIZACAO_VISUALIZAR', 'ROLE_ORGANIZACAO_SIMULAR', 'ROLE_ORGANIZACAO_APROVAR_QODE', 'ROLE_ORGANIZACAO_PUBLICAR_QODE'],
          children: [
            { icon: 'dashboard', label: 'Workspace', route: '/organizacao', roles: ['ROLE_ORGANIZACAO_VISUALIZAR', 'ROLE_ORGANIZACAO_APROVAR_QODE', 'ROLE_ORGANIZACAO_PUBLICAR_QODE'] },
            { icon: 'account_tree', label: 'Estrutura Organizacional', route: '/organizacao/estrutura', roles: ['ROLE_ORGANIZACAO_VISUALIZAR', 'ROLE_ORGANIZACAO_APROVAR_QODE', 'ROLE_ORGANIZACAO_PUBLICAR_QODE'] },
            { icon: 'manage_accounts', label: 'Cargos e Funções', route: '/organizacao/funcoes', roles: ['ROLE_ORGANIZACAO_VISUALIZAR'] },
            { icon: 'assignment_turned_in', label: 'Publicar Portaria', route: '/organizacao/publicar', roles: ['ROLE_ORGANIZACAO_SIMULAR'] },
            { icon: 'history', label: 'Histórico & Time-Travel', route: '/organizacao/historico', roles: ['ROLE_ORGANIZACAO_VISUALIZAR'] },
            { icon: 'view_list', label: 'Catálogo de Quadros', route: '/organizacao/catalogo-quadros', roles: ['ROLE_ORGANIZACAO_VISUALIZAR'] },
            { icon: 'rate_review', label: 'Análise de QODE', route: '/organizacao/estrutura', roles: ['ROLE_ORGANIZACAO_APROVAR_QODE', 'ROLE_ORGANIZACAO_PUBLICAR_QODE'] },
            { label: 'RELATÓRIOS', isHeader: true },
            { icon: 'donut_large', label: 'Atividade-Meio vs Fim', route: '/organizacao/relatorios/atividade-meio-fim', roles: ['ROLE_ORGANIZACAO_VISUALIZAR'] },
          ],
        },
        {
          icon: 'payments',
          label: 'Pagamento',
          roles: ['ROLE_PAGAMENTO_LEITURA', 'ROLE_PAGAMENTO_ESCRITA', 'ROLE_PAGAMENTO_EXPORTACAO', 'ROLE_PAGAMENTO_EXPORTACAO_SAUDE'],
          children: [
            { icon: 'dashboard', label: 'Painel Financeiro', route: '/pagamento/dashboard', roles: [] },
            { icon: 'upload_file', label: 'Exportação SIAPE', route: '/pagamento/exportacao-siape', roles: ['ROLE_PAGAMENTO_EXPORTACAO', 'ROLE_PAGAMENTO_EXPORTACAO_SAUDE'] },
            { icon: 'health_and_safety', label: 'Exportação Esculápio', route: '/pagamento/exportacao-esculapio', roles: ['ROLE_PAGAMENTO_EXPORTACAO_SAUDE'] },
            { icon: 'history', label: 'Histórico Financeiro', route: '/pagamento/historico/08.421-X', roles: ['ROLE_PAGAMENTO_LEITURA'] },
            { icon: 'account_balance_wallet', label: 'Livro Razão', route: '/pagamento/livro-razao', roles: ['ROLE_PAGAMENTO_LEITURA'] },
            { icon: 'insights', label: 'Planejamento', route: '/pagamento/planejamento-financeiro', roles: ['ROLE_PAGAMENTO_LEITURA', 'ROLE_PAGAMENTO_ESCRITA'] },
            { icon: 'flight_takeoff', label: 'Gestão de Diárias', route: '/pagamento/diarias/concessoes', roles: ['ROLE_PAGAMENTO_LEITURA', 'ROLE_PAGAMENTO_ESCRITA'] },
            { icon: 'description', label: 'Prestação de Contas', route: '/pagamento/diarias/prestacao-contas', roles: ['ROLE_PAGAMENTO_LEITURA', 'ROLE_PAGAMENTO_ESCRITA'] },
            { icon: 'security', label: 'Auditoria Financeira', route: '/pagamento/auditoria', roles: ['ROLE_PAGAMENTO_LEITURA', 'ROLE_PAGAMENTO_ESCRITA', 'ROLE_PAGAMENTO_EXPORTACAO', 'ROLE_PAGAMENTO_EXPORTACAO_SAUDE'] },
            { icon: 'notifications_active', label: 'Central de Alertas', route: '/pagamento/alertas', roles: [] },
            { icon: 'settings', label: 'Parâmetros', route: '/pagamento/parametros', roles: ['ROLE_PAGAMENTO_LEITURA', 'ROLE_PAGAMENTO_ESCRITA'] }
          ]
        },
        {
          icon: 'military_tech',
          label: 'Promoções',
          roles: [
            'ROLE_PROMOCAO_VISUALIZAR_QUADRO_ANTIGUIDADE',
            'ROLE_PROMOCAO_GERENCIAR_QUADRO_ANTIGUIDADE',
            'ROLE_PROMOCAO_VISUALIZAR_MERECIMENTO',
            'ROLE_PROMOCAO_GERENCIAR_MERECIMENTO',
            'ROLE_PROMOCAO_VISUALIZAR_PROCESSOS_BRAVURA',
            'ROLE_PROMOCAO_VISUALIZAR_DASHBOARD',
            'ROLE_PROMOCAO_GERENCIAR_ALERTAS',
            'ROLE_PROMOCAO_GERENCIAR_VAGAS',
            'ROLE_PROMOCAO_GERENCIAR_PARAMETROS',
            'ROLE_PROMOCAO_GERENCIAR_RESSARCIMENTO_PRETERICAO',
            'ROLE_PROMOCAO_VISUALIZAR_ALMANAQUE',
            'ROLE_PROMOCAO_GERENCIAR_ALMANAQUE',
            'ROLE_PROMOCAO_SIMULAR_CENARIOS',
            'ROLE_PROMOCAO_REGISTRAR_AVALIACAO',
          ],
          children: [
            { icon: 'dashboard', label: 'Dashboard Executivo', route: '/promocao/dashboard', roles: [] },
            { icon: 'event_note', label: 'Ciclos e Vagas', route: '/promocao/ciclos-vagas', roles: [] },
            { icon: 'list', label: 'Quadro Antiguidade', route: '/promocao/quadro-acesso-antiguidade', roles: [] },
            { icon: 'settings', label: 'Gestão Antiguidade', route: '/promocao/gestao-quadro-antiguidade', roles: [] },
            { icon: 'stars', label: 'Meu Painel (Extrato)', route: '/promocao/pontuacao-merecimento', roles: [] },
            { icon: 'settings_suggest', label: 'Gestão Merecimento', route: '/promocao/gestao-merecimento', roles: [] },
            { icon: 'timeline', label: 'Processos Especiais', route: '/promocao/processos-bravura-post-mortem', roles: [] },
            { icon: 'book', label: 'Almanaque da Tropa', route: '/promocao/almanaque', roles: [] },
            { icon: 'analytics', label: 'Simulador de Cenários', route: '/promocao/simulacao', roles: [] },
            { icon: 'tune', label: 'Parametrização', route: '/promocao/parametros', roles: [] },
            { icon: 'rate_review', label: 'Avaliação Desempenho', route: '/promocao/avaliacao-desempenho', roles: [] }
          ]
        },
        {
          icon: 'security',
          label: 'Segurança e Auditoria',
          roles: ['ROLE_AUDIT_VISUALIZAR', 'ROLE_AUDIT_CORREGEDORIA', 'ROLE_AUDIT_ADMIN'],
          children: [
            { icon: 'dashboard', label: 'Dashboard Executivo', route: '/auditoria/dashboard', roles: ['ROLE_AUDIT_VISUALIZAR'] },
            { icon: 'history', label: 'Consulta de Eventos', route: '/auditoria/eventos', roles: ['ROLE_AUDIT_VISUALIZAR'] },
            { icon: 'notifications_active', label: 'Alertas e Anomalias', route: '/auditoria/alertas', roles: ['ROLE_AUDIT_CORREGEDORIA'] },
            { icon: 'cleaning_services', label: 'Retenção e Expurgo', route: '/auditoria/expurgo', roles: ['ROLE_AUDIT_ADMIN'] },
          ]
        },
        {
          icon: 'schedule',
          label: 'Tempo de Serviço',
          roles: ['ROLE_TEMPO_SERVICO_VISUALIZAR_COMPLETO', 'ROLE_TEMPO_SERVICO_DASHBOARD'],
          children: [
            { icon: 'description', label: 'Minha Ficha', route: '/tempo-servico/meu-tempo', roles: [] },
            { icon: 'calculate', label: 'Calculadora', route: '/tempo-servico/calculadora', roles: [] },
            { icon: 'analytics', label: 'Dashboard Geral', route: '/tempo-servico/dashboard', roles: ['ROLE_TEMPO_SERVICO_DASHBOARD'] },
            { icon: 'query_stats', label: 'Macro-Simulador', route: '/tempo-servico/dashboard/macro-simulador', roles: ['ROLE_TEMPO_SERVICO_DASHBOARD'] },
            { icon: 'list', label: 'Lista Averbações', route: '/tempo-servico/averbacoes', roles: ['ROLE_TEMPO_SERVICO_VISUALIZAR_COMPLETO'] },
            { icon: 'add_circle', label: 'Nova Averbação', route: '/tempo-servico/averbacoes/nova', roles: ['ROLE_TEMPO_SERVICO_AVERBAR'] }
          ]
        },
        {
          icon: 'help_outline',
          label: 'Ajuda / Manuais',
          route: '/wiki-sgp'
        }
      ],
    }
  ]);

  toggleSubmenu(label: string): void {
    this.expandedItems.update(set => {
      const next = new Set(set);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  }

  isExpanded(label: string): boolean {
    return this.expandedItems().has(label);
  }
  /** Filtra grupos visíveis com base nas roles do usuário */
  readonly visibleGroups = computed<any[]>(() => {
    this.auth.user();
    return this.menuGroups()
      .filter((g: NavGroup) => this.canShow(g.roles))
      .map((g: NavGroup) => ({
        ...g,
        items: g.items
          .filter((item: NavItem) => this.canShow(item.roles))
          .map((item: NavItem) => item.children
            ? { ...item, children: item.children.filter((c: NavItem) => this.canShow(c.roles)) }
            : item
          )
          .filter((item: NavItem) => !item.children || item.children.length > 0),
      }))
      .filter((g: any) => g.items.length > 0);
  });

  private canShow(roles?: string[]): boolean {
    return !roles || roles.length === 0 || this.auth.hasAnyRole(roles);
  }
}
